import { API_BASE_URL } from './client';
import { buildStatisticsQueryParams } from '../../utils/timeRange';

export const getStatistics = async (keycloak, timeRange, showAll = false) => {
  if (!keycloak) {
    console.error('getStatistics: Keycloak instance is undefined');
    return null;
  }

  try {
    if (!keycloak.authenticated) {
      throw new Error('User is not authenticated');
    }

    const params = buildStatisticsQueryParams(timeRange, showAll);

    const response = await fetch(`${API_BASE_URL}/statistics/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}, StatusText: ${response.statusText}`);
    }

    const text = await response.text();

    let data;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (parseError) {
      console.error('JSON parse error:', parseError);
      return null;
    }
    return data;

  } catch (error) {
    console.error('Error in getStatistics:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
    });
    return null;
  }
};

export const getTokenUsageOverTime = async (keycloak, timeRange, showAll = false) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('User is not authenticated');
  }

  try {
    const params = buildStatisticsQueryParams(timeRange, showAll);

    const response = await fetch(`${API_BASE_URL}/statistics/tokens/over-time/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching token usage over time:', error);
    return [];
  }
};

export const getRequestsOverTime = async (keycloak, timeRange, showAll = false) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('User is not authenticated');
  }

  try {
    const params = buildStatisticsQueryParams(timeRange, showAll);

    const response = await fetch(`${API_BASE_URL}/statistics/requests/over-time/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching requests over time:', error);
    return [];
  }
};

export const getAssistantUsageDistribution = async (keycloak, timeRange, showAll = false) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('Not authenticated');
  }

  try {
    const params = buildStatisticsQueryParams(timeRange, showAll);

    const response = await fetch(`${API_BASE_URL}/statistics/assistants/usage-distribution/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching assistant usage distribution:', error);
    return [];
  }
};

export const getModelLatency = async (keycloak, timeRange, showAll = false) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('Not authenticated');
  }

  try {
    const params = buildStatisticsQueryParams(timeRange, showAll);

    const response = await fetch(`${API_BASE_URL}/statistics/models/latency/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching model latency:', error);
    return [];
  }
};

export const getCloudSpendOverTime = async (keycloak, timeRange, showAll = false) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('User is not authenticated');
  }

  try {
    const params = buildStatisticsQueryParams(timeRange, showAll);

    const response = await fetch(`${API_BASE_URL}/statistics/cloud-spend/over-time/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching cloud spend over time:', error);
    return [];
  }
};

export const getRecentActivity = async (keycloak, limit = 10, offset = 0, showAll = false) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('Not authenticated');
  }

  try {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    params.append('period', 'allTime');
    if (showAll) {
      params.append('show_all', 'true');
    }

    const response = await fetch(`${API_BASE_URL}/statistics/recent-activity/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching recent activity:', error);
    return { activities: [], total: 0, limit: limit, offset: offset };
  }
};

function buildUsageLogParams({ timeRange, showAll, limit, offset, channel, assistantId, model, status, costSource }) {
  const params = buildStatisticsQueryParams(timeRange, showAll);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  if (channel) params.append('channel', channel);
  if (assistantId) params.append('assistant_id', String(assistantId));
  if (model) params.append('model', model);
  if (status) params.append('status', status);
  if (costSource) params.append('cost_source', costSource);
  return params;
}

export const getModelDistribution = async (keycloak, timeRange, showAll = false) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('User is not authenticated');
  }
  try {
    const params = buildStatisticsQueryParams(timeRange, showAll);
    const response = await fetch(`${API_BASE_URL}/statistics/models/distribution/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching model distribution:', error);
    return [];
  }
};

export const getSpendByAssistant = async (keycloak, timeRange, showAll = false) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('User is not authenticated');
  }
  try {
    const params = buildStatisticsQueryParams(timeRange, showAll);
    const response = await fetch(`${API_BASE_URL}/statistics/spend/by-assistant/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching spend by assistant:', error);
    return [];
  }
};

export const getUsageLogs = async (keycloak, {
  timeRange,
  showAll = false,
  limit = 20,
  offset = 0,
  channel = null,
  assistantId = null,
  model = null,
  status = null,
  costSource = null,
} = {}) => {
  if (!keycloak || !keycloak.authenticated) {
    throw new Error('User is not authenticated');
  }
  try {
    const params = buildUsageLogParams({
      timeRange,
      showAll,
      limit,
      offset,
      channel,
      assistantId,
      model,
      status,
      costSource,
    });
    const response = await fetch(`${API_BASE_URL}/statistics/usage-logs/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching usage logs:', error);
    return { items: [], total: 0 };
  }
};
